import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from "react"

import get from "lodash.get"

import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from "@mui/material"

import { camelCaseToSpaceSeparated } from "utils/stringUtils"

import { cloneFormData } from "./formUtils"

const DIALOG_TRANSITION_PROPS: ComponentProps<typeof Dialog>["TransitionProps"] = { unmountOnExit: true }

export interface ArrayItemCopyDefinition {
  currentItemIndex: number
  fieldDepth: string[]
  itemTitleProperty?: string
  uncopyableProperties?: string[]
}

interface Props<T> {
  formData: T
  copyDefinition: ArrayItemCopyDefinition
  onCopyAndReplace: (fieldDepth: string[], item: unknown) => void
  onDismiss: () => void
}

export default function ArrayItemCopyAndReplaceModal<T>(props: Props<T>) {
  const { formData, copyDefinition, onCopyAndReplace, onDismiss } = props
  const [copyTarget, setCopyTarget] = useState<string>("")

  const { copyOptions, itemTitle, parentArray } = useMemo(() => {
    if (!copyDefinition) return {}

    const { currentItemIndex, fieldDepth, itemTitleProperty } = copyDefinition

    const itemTitle = camelCaseToSpaceSeparated(fieldDepth[fieldDepth.length - 1]).toLowerCase()

    const parentArray: unknown[] = get(formData, fieldDepth)

    const copyOptions = parentArray.map((item, index) => {
      let label = `${itemTitle} ${index + 1}`
      const title: string | undefined = item[itemTitleProperty]

      if (title) {
        label += ` [${title}]`
      }

      const isCurrent = index === currentItemIndex

      if (isCurrent) {
        label += " [CURRENT]"
      }

      return {
        disabled: isCurrent,
        label,
        value: String(index)
      }
    })

    return { copyOptions, itemTitle, parentArray }
  }, [copyDefinition, formData])

  const handleRadioChange = useCallback<ComponentProps<typeof RadioGroup>["onChange"]>((e) => {
    setCopyTarget(e.target.value)
  }, [])

  const handleCopyAndReplace = useCallback(() => {
    const currentItem = parentArray[copyDefinition.currentItemIndex]
    const targetItem = cloneFormData(parentArray[copyTarget])

    copyDefinition.uncopyableProperties.forEach((property) => {
      targetItem[property] = currentItem[property]
    })
    onCopyAndReplace([...copyDefinition.fieldDepth, String(copyDefinition.currentItemIndex)], targetItem)
  }, [copyDefinition, copyTarget, onCopyAndReplace, parentArray])

  useEffect(() => {
    setCopyTarget("")
  }, [copyDefinition])

  if (!copyDefinition) {
    return null
  }

  return (
    <Dialog open={!!copyDefinition} TransitionProps={DIALOG_TRANSITION_PROPS} onClose={onDismiss}>
      <DialogTitle>
        Replacing{" "}
        <Typography color="primary" variant="inherit" component="span">
          {itemTitle} {copyDefinition.currentItemIndex + 1}
        </Typography>{" "}
        with:
      </DialogTitle>
      <DialogContent dividers>
        <RadioGroup value={copyTarget} onChange={handleRadioChange}>
          {copyOptions?.map((itemOption) => (
            <FormControlLabel
              key={`item-${itemOption.value}`}
              value={itemOption.value}
              disabled={itemOption.disabled}
              control={<Radio />}
              label={itemOption.label}
            />
          ))}
          {!copyOptions?.length && <Alert severity="warning">No sibling available</Alert>}
        </RadioGroup>
        {!!copyDefinition?.uncopyableProperties?.length && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="caption">These properties won't be copied:</Typography>
              {copyDefinition.uncopyableProperties.map((property, index) => (
                <Chip key={`${property}-${index}`} size="small" variant="outlined" label={property} />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss}>Dismiss</Button>
        <Button variant="contained" disabled={!copyTarget} onClick={handleCopyAndReplace}>
          Copy and replace
        </Button>
      </DialogActions>
    </Dialog>
  )
}
